import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, API } from "@/lib/api";
import { toast } from "sonner";
import { X, UploadSimple, FileArrowUp } from "@phosphor-icons/react";

const SISTEMAS_ORIGEM = [
  "Banco de Preços",
  "M2A Tecnologia",
  "Painel de Preços (Compras.gov.br)",
  "Negocio Público",
  "Planilha Manual",
  "Outro sistema externo",
];

export default function PesquisaExternaForm({ onClose, onSaved }) {
  const [contracts, setContracts] = useState([]);
  const [contractItens, setContractItens] = useState([]);
  const [form, setForm] = useState({
    contract_id: "",
    contract_item_numero: "",
    termo: "",
    sistema_origem: "Banco de Preços",
    preco_referencia: "",
    qtd_amostras: 1,
    menor: "",
    maior: "",
    media: "",
    mediana: "",
    cv: "",
    observacao: "",
  });
  const [file, setFile] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api.get("/contracts?limit=200").then((r) => setContracts(r.data.items || []));
  }, []);

  useEffect(() => {
    if (!form.contract_id) { setContractItens([]); return; }
    api.get(`/contracts/${form.contract_id}`)
      .then((r) => setContractItens(r.data?.itens || []))
      .catch(() => setContractItens([]));
  }, [form.contract_id]);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.contract_id || !form.termo || !form.preco_referencia) {
      toast.error("Preencha contrato, termo e preço de referência");
      return;
    }
    setSaving(true);
    try {
      const payload = {
        contract_id: form.contract_id,
        contract_item_numero: form.contract_item_numero ? parseInt(form.contract_item_numero, 10) : null,
        termo: form.termo,
        sistema_origem: form.sistema_origem,
        preco_referencia: parseFloat(form.preco_referencia),
        qtd_amostras: parseInt(form.qtd_amostras, 10) || 1,
        menor: form.menor ? parseFloat(form.menor) : null,
        maior: form.maior ? parseFloat(form.maior) : null,
        media: form.media ? parseFloat(form.media) : null,
        mediana: form.mediana ? parseFloat(form.mediana) : null,
        cv: form.cv ? parseFloat(form.cv) : null,
        observacao: form.observacao,
      };
      const { data: pesq } = await api.post("/pesquisas-precos/externa", payload);
      // Upload anexo se houver
      if (file) {
        const fd = new FormData();
        fd.append("file", file);
        fd.append("descricao", `Comprovante - ${form.sistema_origem}`);
        const res = await fetch(`${API}/pesquisas-precos/${pesq.id}/anexar`, {
          method: "POST", body: fd, credentials: "include",
        });
        if (!res.ok) toast.warning("Pesquisa criada, mas falha ao anexar arquivo");
      }
      toast.success("Pesquisa externa importada com sucesso");
      onSaved && onSaved(pesq);
      onClose();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Erro ao importar");
    } finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 z-[100] bg-black/80 backdrop-blur-sm flex items-center justify-center p-4" onClick={onClose}>
      <div className="tce-card w-full max-w-2xl max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between px-6 py-5 border-b border-[#334155]">
          <div className="flex items-center gap-2">
            <FileArrowUp size={20} weight="duotone" className="text-[#F59E0B]" />
            <div className="font-heading text-xl">Importar pesquisa externa</div>
          </div>
          <button onClick={onClose} className="text-[#94A3B8] hover:text-[#F59E0B]" data-testid="close-extern"><X size={20} /></button>
        </div>
        <div className="px-6 py-3 bg-[#1E3A8A]/15 border-b border-[#1E3A8A]/30 text-xs text-[#94A3B8]">
          Use este formulário para registrar pesquisas realizadas em <strong className="text-[#FCD34D]">Banco de Preços, M2A Tecnologia, planilhas manuais</strong> ou outros sistemas externos.
          Anexe o PDF/Excel de comprovação no campo abaixo.
        </div>
        <form onSubmit={submit} className="px-6 py-5 space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="uppercase-label">Contrato vinculado *</label>
              <select data-testid="extern-contract" value={form.contract_id}
                      onChange={(e) => setForm({ ...form, contract_id: e.target.value, contract_item_numero: "" })}
                      className="tce-input mt-1 w-full px-3 py-2 rounded-md text-sm" required>
                <option value="">Selecione um contrato...</option>
                {contracts.map((c) => (
                  <option key={c.id} value={c.id}>{c.numero} — {(c.objeto || "").slice(0, 60)}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="uppercase-label">Item do contrato (opcional)</label>
              <select data-testid="extern-item" value={form.contract_item_numero}
                      onChange={(e) => setForm({ ...form, contract_item_numero: e.target.value })}
                      className="tce-input mt-1 w-full px-3 py-2 rounded-md text-sm">
                <option value="">Pesquisa do contrato inteiro</option>
                {contractItens.map((it) => (
                  <option key={it.numero_item} value={it.numero_item}>
                    Item {it.numero_item} — {(it.descricao || "").slice(0, 50)}
                  </option>
                ))}
              </select>
            </div>
          </div>

          <div>
            <label className="uppercase-label">Termo / Objeto pesquisado *</label>
            <input data-testid="extern-termo" value={form.termo}
                   onChange={(e) => setForm({ ...form, termo: e.target.value })}
                   placeholder="Ex: Detergente neutro 5L, Vassoura de pelo natural..."
                   className="tce-input mt-1 w-full px-3 py-2 rounded-md text-sm" required maxLength={300} />
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="uppercase-label">Sistema de origem *</label>
              <select data-testid="extern-sistema" value={form.sistema_origem}
                      onChange={(e) => setForm({ ...form, sistema_origem: e.target.value })}
                      className="tce-input mt-1 w-full px-3 py-2 rounded-md text-sm" required>
                {SISTEMAS_ORIGEM.map((s) => <option key={s}>{s}</option>)}
              </select>
            </div>
            <div>
              <label className="uppercase-label">Qtd. de amostras *</label>
              <input data-testid="extern-qtd" type="number" min="1" value={form.qtd_amostras}
                     onChange={(e) => setForm({ ...form, qtd_amostras: e.target.value })}
                     className="tce-input mt-1 w-full px-3 py-2 rounded-md text-sm" required />
            </div>
          </div>

          <div className="bg-[#0B1220] border border-[#334155] rounded-md p-4">
            <div className="uppercase-label !text-[#F59E0B] mb-3">Estatísticas apuradas (IN 65/2021)</div>
            <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
              <div>
                <label className="text-[10px] text-[#94A3B8]">Preço-referência (R$) *</label>
                <input data-testid="extern-ref" type="number" step="0.01" value={form.preco_referencia}
                       onChange={(e) => setForm({ ...form, preco_referencia: e.target.value })}
                       className="tce-input mt-1 w-full px-2 py-1.5 rounded text-sm" required />
              </div>
              <div>
                <label className="text-[10px] text-[#94A3B8]">Menor</label>
                <input type="number" step="0.01" value={form.menor}
                       onChange={(e) => setForm({ ...form, menor: e.target.value })}
                       className="tce-input mt-1 w-full px-2 py-1.5 rounded text-sm" />
              </div>
              <div>
                <label className="text-[10px] text-[#94A3B8]">Maior</label>
                <input type="number" step="0.01" value={form.maior}
                       onChange={(e) => setForm({ ...form, maior: e.target.value })}
                       className="tce-input mt-1 w-full px-2 py-1.5 rounded text-sm" />
              </div>
              <div>
                <label className="text-[10px] text-[#94A3B8]">Média</label>
                <input type="number" step="0.01" value={form.media}
                       onChange={(e) => setForm({ ...form, media: e.target.value })}
                       className="tce-input mt-1 w-full px-2 py-1.5 rounded text-sm" />
              </div>
              <div>
                <label className="text-[10px] text-[#94A3B8]">Mediana</label>
                <input type="number" step="0.01" value={form.mediana}
                       onChange={(e) => setForm({ ...form, mediana: e.target.value })}
                       className="tce-input mt-1 w-full px-2 py-1.5 rounded text-sm" />
              </div>
              <div>
                <label className="text-[10px] text-[#94A3B8]">CV (%)</label>
                <input type="number" step="0.1" value={form.cv}
                       onChange={(e) => setForm({ ...form, cv: e.target.value })}
                       className="tce-input mt-1 w-full px-2 py-1.5 rounded text-sm" />
              </div>
            </div>
          </div>

          <div>
            <label className="uppercase-label">Arquivo de comprovação (PDF / Excel / Imagem — máx 5 MB)</label>
            <input data-testid="extern-file" type="file" accept=".pdf,.xlsx,.xls,.csv,.json,.jpg,.jpeg,.png,.webp"
                   onChange={(e) => setFile(e.target.files?.[0] || null)}
                   className="mt-1 w-full text-xs text-[#94A3B8] file:mr-3 file:py-2 file:px-4 file:rounded file:border-0 file:bg-[#F59E0B] file:text-[#0F172A] file:font-semibold file:cursor-pointer" />
            {file && <div className="text-[10px] text-[#10B981] mt-1">📎 {file.name} ({(file.size / 1024).toFixed(1)} KB)</div>}
          </div>

          <div>
            <label className="uppercase-label">Observação (opcional)</label>
            <textarea value={form.observacao} onChange={(e) => setForm({ ...form, observacao: e.target.value })}
                      placeholder="Ex.: Cotação Banco de Preços ref. Q2/2026..."
                      className="tce-input mt-1 w-full px-3 py-2 rounded-md text-sm" rows={2} maxLength={500} />
          </div>

          <div className="flex items-center justify-end gap-3 pt-3 border-t border-[#334155]">
            <button type="button" onClick={onClose} className="px-4 py-2 text-xs text-[#94A3B8] hover:text-[#E9EEF5]">
              Cancelar
            </button>
            <button type="submit" disabled={saving} data-testid="extern-submit"
                    className="px-5 py-2 rounded-md bg-[#F59E0B] text-[#0F172A] hover:bg-[#FCD34D] font-medium text-sm flex items-center gap-2 disabled:opacity-50">
              <UploadSimple size={14} weight="duotone" /> {saving ? "Importando..." : "Importar pesquisa"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
